import { type ClassValue, clsx } from 'clsx';
import { format } from 'date-fns';
import { twMerge } from 'tailwind-merge';
import Holidays from 'date-holidays';
import type { HolidaysTypes } from 'date-holidays';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const hd = new Holidays('SE');

const dateFormatter = new Intl.DateTimeFormat('sv-SE', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

export function formatDateLocal(
  date: Date | string | null | undefined,
): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  return dateFormatter.format(d); // ex: "2025-06-20"
}

export function toLocalISOString(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
}

// --- Safari/iOS safe date/time formatting helpers ---
type DateStyle = 'short' | 'medium' | 'long' | 'full';
type TimeStyle = 'short' | 'medium' | 'long' | 'full';

const hasDateStyleSupport: boolean = (() => {
  try {
    // Some older Safari versions throw when using dateStyle/timeStyle
    new Intl.DateTimeFormat('en-US', { dateStyle: 'short' } as Intl.DateTimeFormatOptions).format(
      new Date(),
    );
    return true;
  } catch {
    return false;
  }
})();

function mapDateStyle(style: DateStyle): Intl.DateTimeFormatOptions {
  // Fallback mappings approximating built-in styles
  switch (style) {
    case 'full':
    case 'long':
      return { year: 'numeric', month: 'long', day: 'numeric' };
    case 'medium':
      return { year: 'numeric', month: 'short', day: 'numeric' };
    case 'short':
    default:
      return { year: '2-digit', month: '2-digit', day: '2-digit' };
  }
}

function mapTimeStyle(style: TimeStyle): Intl.DateTimeFormatOptions {
  switch (style) {
    case 'full':
    case 'long':
      return { hour: '2-digit', minute: '2-digit', second: '2-digit' };
    case 'medium':
      return { hour: '2-digit', minute: '2-digit', second: '2-digit' };
    case 'short':
    default:
      return { hour: '2-digit', minute: '2-digit' };
  }
}

export function formatDateStyle(
  value: string | number | Date,
  style: DateStyle = 'short',
  locale = 'sv-SE',
): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  if (hasDateStyleSupport) {
    return new Intl.DateTimeFormat(locale, { dateStyle: style }).format(d);
  }
  const opts: Intl.DateTimeFormatOptions = mapDateStyle(style);
  return new Intl.DateTimeFormat(locale, opts).format(d);
}

export function formatTimeStyle(
  value: string | number | Date,
  style: TimeStyle = 'short',
  locale = 'sv-SE',
): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  if (hasDateStyleSupport) {
    return new Intl.DateTimeFormat(locale, { timeStyle: style }).format(d);
  }
  const opts: Intl.DateTimeFormatOptions = mapTimeStyle(style);
  return new Intl.DateTimeFormat(locale, opts).format(d);
}

/**
 * Parse a date time string as local time regardless of timezone information.
 * Any timezone offset is stripped before creating the `Date` instance so that
 * the time portion remains unchanged.
 */
export function parseLocalDateTime(value?: string | null): Date | null {
  if (!value) return null;
  const trimmed = value.slice(0, 19);
  const date = new Date(trimmed);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function getSwedishHolidaysBetween(start: Date, end: Date) {
  const rangeStart = new Date(start);
  const rangeEnd = new Date(end);

  if (Number.isNaN(rangeStart.getTime()) || Number.isNaN(rangeEnd.getTime())) {
    return [];
  }

  const years: number[] = [];
  for (let year = rangeStart.getFullYear(); year <= rangeEnd.getFullYear(); year += 1) {
    years.push(year);
  }

  const validTypes = ['public', 'bank'];
  const seen = new Set<string>();
  const holidays: HolidaysTypes.Holiday[] = [];

  for (const year of years) {
    const yearHolidays = hd.getHolidays(year).filter((holiday) =>
      validTypes.includes(holiday.type),
    );

    for (const holiday of yearHolidays) {
      const date = new Date(holiday.date);
      if (date < rangeStart || date > rangeEnd) {
        continue;
      }

      const key = format(date, 'yyyy-MM-dd');
      if (seen.has(key)) {
        continue;
      }

      seen.add(key);
      holidays.push({ ...holiday, date: date.toISOString() });
    }
  }

  return holidays.sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();
    return dateA - dateB;
  });
}

export function isSwedishHoliday(
  date: Date,
  holidays: HolidaysTypes.Holiday[],
): string | null {
  const key = formatDateLocal(date);
  const match = holidays.find((h) => formatDateLocal(h.date) === key);
  return match ? match.name : null;
}

export function calculateExpectedWorkingHours(
  start: Date,
  end: Date,
  holidays: HolidaysTypes.Holiday[] = [],
): number {
  const holidayList =
    holidays.length > 0 ? holidays : getSwedishHolidaysBetween(start, end);
  let hours = 0;
  const current = new Date(start);
  const endDate = new Date(end);

  while (current <= endDate) {
    const dayOfWeek = current.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const isHoliday = !!isSwedishHoliday(current, holidayList);

    if (!isWeekend && !isHoliday) {
      hours += 8;
    }

    current.setDate(current.getDate() + 1);
  }

  return hours;
}
